import type { BuilderWeights, ScoreParts } from './types';
import { BUILDER_WEIGHTS } from './types';

/**
 * How strongly **excess** cancel rate (above baseline) demotes sold mass.
 * Baseline absorbs "normal" cancel noise (~1/3 of corpus); only worse rates pay.
 */
export const CANCEL_RATE_LAMBDA = 0.55;

/** Default cancel-rate baseline when the slice has no cancel evidence yet. */
export const DEFAULT_CANCEL_BASELINE = 0.35;

/**
 * Floor so demotion never zeros a product that still has sold trips.
 */
export const MIN_SOLD_MASS_FACTOR = 0.2;

/**
 * Effective sold mass after rate-based cancel demotion (pure, unit-tested).
 *
 * demote only excess over baseline:
 *   factor = 1 − λ · max(0, r − r̄) / (1 − r̄)
 * mass = sold · max(floor, factor)
 */
export const effectiveSoldMass = (
  soldTrips: number,
  cancelledTrips = 0,
  baselineCancelRate = DEFAULT_CANCEL_BASELINE
): number => {
  const sold = Math.max(0, soldTrips);
  const cancelled = Math.max(0, cancelledTrips);
  if (sold <= 0) return 0;
  const total = sold + cancelled;
  const r = cancelled / total;
  const rBar = Math.min(0.85, Math.max(0, baselineCancelRate));
  const denom = Math.max(1e-6, 1 - rBar);
  const excess = Math.max(0, r - rBar) / denom;
  const factor = 1 - CANCEL_RATE_LAMBDA * excess;
  return sold * Math.max(MIN_SOLD_MASS_FACTOR, factor);
};

/**
 * Thin-slice confidence for popularity (0–1). Low sold counts → less weight
 * on raw popularity so keyword / city / co-occur can surface.
 */
export const popularityConfidence = (soldTrips: number): number => {
  const s = Math.max(0, soldTrips);
  // Full confidence around ~20 sold trips in the conditioned slice.
  return Math.min(1, Math.log(1 + s) / Math.log(1 + 20));
};

/**
 * Decomposed base score (before co-occurrence boost).
 */
export const scoreSoldProductParts = (args: {
  soldTrips: number;
  cancelledTrips?: number;
  keywordHits?: number;
  cityHit?: boolean;
  /** When true, zero keyword hits get a soft penalty and hits are amplified. */
  keywordsActive?: boolean;
  baselineCancelRate?: number;
  weights?: BuilderWeights;
  /** Optional co-occurrence component (filled when boost applied). */
  coOccurrence?: number;
}): ScoreParts => {
  const w = args.weights ?? BUILDER_WEIGHTS;
  const mass = effectiveSoldMass(
    args.soldTrips,
    args.cancelledTrips ?? 0,
    args.baselineCancelRate
  );
  const conf = popularityConfidence(args.soldTrips);
  let popularity = w.popularity * conf * Math.log(1 + mass) * 10;

  const kw = Math.max(0, args.keywordHits ?? 0);
  const keywordsActive = args.keywordsActive === true;
  let keyword = 0;
  if (kw > 0) {
    // Keywords present → lead, not tie-break (amp ~1.75×).
    const amp = keywordsActive ? 1.75 : 1;
    keyword = w.keyword * kw * 10 * amp;
  } else if (keywordsActive) {
    // Soft gate: non-matches still eligible but trail keyword hits.
    // Apply as a popularity discount so the keyword component stays 0.
    popularity *= 0.88;
  }

  const city = args.cityHit ? w.city * 10 : 0;
  const coOccurrence = Math.max(0, args.coOccurrence ?? 0);
  const total = popularity + keyword + city + coOccurrence;
  return { popularity, keyword, city, coOccurrence, total };
};

/**
 * Base product score before co-occurrence boost.
 * Higher sold → higher score; excess cancel rate → lower; keywords lead when set.
 */
export const scoreSoldProduct = (args: {
  soldTrips: number;
  cancelledTrips?: number;
  keywordHits?: number;
  cityHit?: boolean;
  /** When true, zero keyword hits get a soft penalty and hits are amplified. */
  keywordsActive?: boolean;
  baselineCancelRate?: number;
  weights?: BuilderWeights;
}): number => scoreSoldProductParts(args).total;

/**
 * Co-occurrence additive boost. Prefers lift when available, else log coTrips.
 * Breadth (distinct accounts) damps single-account template pairs.
 */
export const coOccurrenceBoost = (
  coTrips: number,
  opts: {
    weight?: number;
    lift?: number | null;
    distinctAccounts?: number | null;
  } = {}
): number => {
  if (coTrips <= 0) return 0;
  const weight = opts.weight ?? BUILDER_WEIGHTS.coOccurrence;
  const lift = opts.lift != null && opts.lift > 0 ? opts.lift : 1;
  const accounts = opts.distinctAccounts ?? 1;
  // Breadth guard: <3 accounts → half credit (R5 soft spirit).
  const breadth = accounts >= 3 ? 1 : accounts >= 2 ? 0.75 : 0.45;
  const liftCap = Math.min(lift, 8);
  return weight * Math.log(1 + coTrips) * Math.log(1 + liftCap) * 4 * breadth;
};
